import type { ReactNode } from 'react';

import { InstagramGlyph, MkPill, WhatsAppGlyph } from './primitives';

/* ------------------------------------------------------------------ *
 * Shared product-UI atoms.
 *
 * These are deliberately schematic: real screenshots will replace whole
 * panels later (see ProductMockup), but each atom is legible on its own so
 * the story survives without any animation.
 * ------------------------------------------------------------------ */

export function WindowChrome({
  title,
  right,
  children,
  className = '',
}: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={`mk-panel mk-edge-light overflow-hidden rounded-2xl ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mk-line)] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
          </span>
          <span className="text-xs font-semibold tracking-wide text-[var(--mk-ink-soft)]">
            {title}
          </span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function ChannelBadge({
  channel,
  withLabel = false,
}: {
  channel: 'whatsapp' | 'instagram';
  withLabel?: boolean;
}): JSX.Element {
  const isWa = channel === 'whatsapp';
  const color = isWa ? 'var(--mk-wa)' : 'var(--mk-ig)';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold"
      style={{ color }}
      title={isWa ? 'WhatsApp' : 'Instagram'}
    >
      {isWa ? (
        <WhatsAppGlyph className="h-3.5 w-3.5" />
      ) : (
        <InstagramGlyph className="h-3.5 w-3.5" />
      )}
      {withLabel ? (
        isWa ? (
          'WhatsApp'
        ) : (
          'Instagram'
        )
      ) : (
        <span className="sr-only">{isWa ? 'WhatsApp' : 'Instagram'}</span>
      )}
    </span>
  );
}

export function ChatBubble({
  from,
  children,
  meta,
  className = '',
}: {
  from: 'customer' | 'ai';
  children: ReactNode;
  meta?: string;
  className?: string;
}): JSX.Element {
  const isCustomer = from === 'customer';
  return (
    <div className={`flex ${isCustomer ? 'justify-start' : 'justify-end'} ${className}`}>
      <div className="max-w-[86%]">
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
            isCustomer
              ? 'rounded-tl-md border border-[var(--mk-line)] bg-[var(--mk-surface-strong)] text-[var(--mk-ink)]'
              : 'rounded-tr-md border border-[color-mix(in_srgb,var(--mk-violet)_36%,transparent)] bg-[linear-gradient(150deg,rgba(77,124,255,0.22),rgba(144,97,255,0.16))] text-[var(--mk-ink)]'
          }`}
        >
          {children}
        </div>
        {meta ? (
          <p
            className={`mt-1.5 text-[11px] text-[var(--mk-ink-muted)] ${
              isCustomer ? 'text-left' : 'text-right'
            }`}
          >
            {meta}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function TypingBubble(): JSX.Element {
  return (
    <div className="flex justify-end">
      <div
        className="flex items-center gap-1 rounded-2xl rounded-tr-md border border-[var(--mk-line)] bg-[var(--mk-surface)] px-4 py-3.5"
        role="status"
        aria-label="Vaanexa is composing a reply"
      >
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="mk-typing-dot h-1.5 w-1.5 rounded-full bg-[var(--mk-ink-soft)]"
            style={{ animationDelay: `${index * 0.16}s` }}
            aria-hidden
          />
        ))}
      </div>
    </div>
  );
}

/** Labelled key/value row used across the lead-intelligence panels. */
export function FieldRow({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'accent';
}): JSX.Element {
  const valueTone =
    tone === 'positive'
      ? 'text-[var(--mk-positive)]'
      : tone === 'accent'
        ? 'text-[#c4b0ff]'
        : 'text-[var(--mk-ink)]';
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--mk-line)] py-2.5 last:border-b-0">
      <dt className="text-xs font-medium text-[var(--mk-ink-muted)]">{label}</dt>
      <dd className={`text-right text-sm font-semibold ${valueTone}`}>{value}</dd>
    </div>
  );
}

/**
 * Lead-score meter. The numeric value is always in the DOM, so the bar is
 * decoration only and the figure is readable without animation.
 */
export function ScoreMeter({
  value,
  label = 'Lead score',
}: {
  value: number;
  label?: string;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-[var(--mk-ink-muted)]">{label}</span>
        <span className="text-sm font-bold text-[#c4b0ff]">{value}%</span>
      </div>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/8"
        role="meter"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className="h-full rounded-full bg-[linear-gradient(90deg,var(--mk-blue),var(--mk-violet),var(--mk-cyan))] transition-[width] duration-[900ms] ease-out"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="mk-card rounded-xl px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--mk-ink-muted)]">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold tracking-[-0.02em] text-[var(--mk-ink)]">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-[var(--mk-ink-muted)]">{hint}</p> : null}
    </div>
  );
}

/** Compact pipeline board used in the hero, journey and bento grid. */
export function PipelineStrip({
  activeStage = 2,
  stages = ['New', 'Qualified', 'Quoted', 'Won'],
}: {
  activeStage?: number;
  stages?: string[];
}): JSX.Element {
  return (
    <ol className="flex items-stretch gap-1.5">
      {stages.map((stage, index) => {
        const done = index < activeStage;
        const current = index === activeStage;
        return (
          <li key={stage} className="flex-1">
            <div
              className={`rounded-lg border px-2 py-2 text-center text-[11px] font-semibold transition-colors duration-500 ${
                current
                  ? 'border-[color-mix(in_srgb,var(--mk-violet)_50%,transparent)] bg-[color-mix(in_srgb,var(--mk-violet)_16%,transparent)] text-[#d3c4ff]'
                  : done
                    ? 'border-[var(--mk-line)] bg-[var(--mk-surface)] text-[var(--mk-ink-soft)]'
                    : 'border-dashed border-[var(--mk-line)] text-[var(--mk-ink-muted)]'
              }`}
            >
              {stage}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Small sparkline. Purely decorative — figures beside it carry the meaning. */
export function Sparkline({
  points = [12, 18, 15, 26, 24, 34, 31, 44, 52],
  className = '',
}: {
  points?: number[];
  className?: string;
}): JSX.Element {
  const max = Math.max(...points, 1);
  const path = points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * 100;
      const y = 30 - (point / max) * 26;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox="0 0 100 32" className={className} preserveAspectRatio="none" aria-hidden>
      <path d={path} fill="none" stroke="url(#mk-spark)" strokeWidth="1.8" strokeLinecap="round" />
      <defs>
        <linearGradient id="mk-spark" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--mk-blue)" />
          <stop offset="60%" stopColor="var(--mk-violet)" />
          <stop offset="100%" stopColor="var(--mk-cyan)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function NotificationCard({
  icon,
  title,
  body,
  tone = 'default',
}: {
  icon: ReactNode;
  title: string;
  body: string;
  tone?: 'default' | 'positive' | 'accent';
}): JSX.Element {
  return (
    <div className="mk-panel flex items-start gap-3 rounded-xl px-3.5 py-3">
      <span className="mt-0.5 text-[var(--mk-ink-soft)]" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-[var(--mk-ink)]">{title}</p>
          {tone !== 'default' ? <MkPill tone={tone}>Auto</MkPill> : null}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-[var(--mk-ink-muted)]">{body}</p>
      </div>
    </div>
  );
}
